/**
 * Mount commands for a file share (plan Task 7.2, D11 / R6): text only, NEVER a key or a SAS — the SMB commands carry
 * the literal placeholder `<storage-account-key>` and say where to find the key. Strings follow Learn (fetched
 * 2026-09-25): "Mount SMB Azure file share on Windows" (cmdkey + New-PSDrive), "on Linux" (credentials file + cifs
 * options), "on macOS" (open smb://…, which prompts for the key), "Mount an NFS Azure file share on Linux". NFS is
 * Linux-only. One deliberate departure from Learn: the Linux credentials file is created mode 600 before it is
 * written (Learn writes it, then chmods it). Process-agnostic.
 */
import type { FileShareProtocol, MountCommandVariant } from '../../types/azureStorage';

export const MOUNT_KEY_PLACEHOLDER = '<storage-account-key>';
const KEY_NOTE = 'Replace <storage-account-key> with a key from the storage account: Azure portal → Security + networking → Access keys. Jam SQL never copies the key.';
const SMB_PORT_NOTE = 'SMB needs outbound TCP port 445; many internet providers block it (use a VPN or ExpressRoute then).';
const NFS_NETWORK_NOTE = 'NFS shares are reachable only from a virtual network or private endpoint the storage account allows, over TCP port 2049; they cannot be mounted over the internet.';
const NFS_TLS_NOTE = "The native mount sends NFS without TLS, which needs 'Secure transfer required' turned off on the account; the Azure portal's Connect from Linux script mounts with encryption in transit through the AZNFS mount helper instead.";

const sh = (value: string): string => `'${value.replace(/'/gu, `'\\''`)}'`;
const ps = (value: string): string => `"${value.replace(/[`"$]/gu, '`$&')}"`;

export function mountCommandsFor(target: { account: string; fileHost?: string; share: string; protocol: FileShareProtocol; emulator?: boolean }): MountCommandVariant[] {
  const { account, fileHost: host, share } = target;
  if (target.emulator || !host) return [];
  if (target.protocol === 'nfs') {
    const mountPoint = `/mount/${account}/${share}`;
    return [{
      os: 'linux', protocol: 'nfs', label: 'Linux (NFS 4.1)',
      command: [`sudo mkdir -p ${sh(mountPoint)}`, `sudo mount -t nfs ${sh(`${host}:/${account}/${share}`)} ${sh(mountPoint)} -o vers=4,minorversion=1,sec=sys,nconnect=4`].join('\n'),
      notes: [NFS_NETWORK_NOTE, NFS_TLS_NOTE],
    }];
  }
  const credentials = `/etc/smbcredentials/${account}.cred`;
  const linuxMount = `/media/${account}/${share}`;
  return [
    {
      os: 'windows', protocol: 'smb', label: 'Windows (PowerShell)',
      command: [
        `cmdkey /add:${ps(host)} /user:${ps(`localhost\\${account}`)} /pass:${ps(MOUNT_KEY_PLACEHOLDER)}`,
        `New-PSDrive -Name Z -PSProvider FileSystem -Root ${ps(`\\\\${host}\\${share}`)} -Persist -Scope global`,
      ].join('\n'),
      notes: [KEY_NOTE, SMB_PORT_NOTE],
    },
    {
      os: 'macos', protocol: 'smb', label: 'macOS (Terminal)',
      command: `open ${sh(`smb://${account}@${host}/${share}`)}`,
      notes: ['macOS asks for a password: enter the storage account key (Azure portal → Security + networking → Access keys).', SMB_PORT_NOTE],
    },
    {
      os: 'linux', protocol: 'smb', label: 'Linux (SMB 3.1.1)',
      command: [
        `sudo mkdir -p ${sh(linuxMount)} /etc/smbcredentials`,
        // Created empty with mode 600 FIRST, so the key never sits in a world-readable file (review A finding 7);
        // `tee` truncates the existing file and keeps its mode.
        `sudo install -m 600 /dev/null ${sh(credentials)}`,
        `printf 'username=%s\\npassword=%s\\n' ${sh(account)} ${sh(MOUNT_KEY_PLACEHOLDER)} | sudo tee ${sh(credentials)} > /dev/null`,
        `sudo mount -t cifs ${sh(`//${host}/${share}`)} ${sh(linuxMount)} -o credentials=${credentials},serverino,nosharesock,actimeo=30,mfsymlinks`,
      ].join('\n'),
      notes: [KEY_NOTE, 'Kernels older than 5.0 need vers=3.1.1 added to the mount options.', SMB_PORT_NOTE],
    },
  ];
}
